import { Box, Chip, FormControl, FormHelperText, FormLabel, ListSubheader, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useEffect, useRef, useState } from "react";
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import TooltipCompetenceDate from "./paymentConditions/TooltipCompetenceDate";
import TooltipInterest from "./paymentConditions/TooltipInterest";
import TooltipFine from "./paymentConditions/TooltipFine";
import Decimal from 'decimal.js';
import CurrencyTextField from "../../../components/shared/fields/CurrencyTextField";
import DataRequiredMessageError from "./paymentConditions/DataRequiredMessageError";

const PaymentsTypeOptions = [
    { group: 'automated', value: 'boleto_automatic', label: 'Boleto bancário' },
    { group: 'automated', value: 'credit_card_automatic', label: 'Cartão de crédito (via link)' },
    { group: 'automated', value: 'pix_automatic', label: 'PIX' },
    { group: 'manual', value: 'credit_card', label: 'Cartão de crédito' },
    { group: 'manual', value: 'debit_card', label: 'Cartão de débito' },
    { group: 'manual', value: 'pix', label: 'PIX' },
    { group: 'manual', value: 'boleto', label: 'Boleto bancário' },
    { group: 'manual', value: 'cash', label: 'Dinheiro' },
];


const groupedOptions = PaymentsTypeOptions.reduce((acc, opt) => {
    if (!acc[opt.group]) acc[opt.group] = [];
    acc[opt.group].push(opt);
    return acc;
}, {});

const installmentsTooltipMap = {
    credit_card_automatic: "Opções de parcelamento serão exibidas no link de pagamento da fatura",
    boleto_automatic: "Não é possível criar parcelas para a forma de pagamento selecionada",
    pix_automatic: "Não é possível criar parcelas para a forma de pagamento selecionada",
};

const PaymentInstallments = [
    'À vista',
    ...Array.from({ length: 31 }, (_, i) => `${i + 2}x`)
];

const DiscountOptions = [
    { value: 'fixed', label: 'R$' },
    { value: 'percentage', label: '%' },
];

const calculateDiscount = (partsValue, worksValue, discountType, discountValue) => {
    const total = new Decimal(partsValue).plus(worksValue);
    const discountVal = new Decimal(discountValue || 0);
    let discountAmount = new Decimal(0);

    if (discountType === 'percentage') {
        discountAmount = total.mul(discountVal).div(100).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
    } else if (discountType === 'fixed') {
        discountAmount = discountVal.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
    }

    const totalWithDiscount = total.minus(discountAmount).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);

    return { total, discountAmount, totalWithDiscount };
};


const recalcInstallments = (values, manualCopy, totalWithDiscount) => {
    const qty = values.length;
    const autoIndexes = values.map((_, i) => !manualCopy[i] ? i : null).filter(i => i !== null);

    const sumManual = values.reduce((acc, val, i) => manualCopy[i] ? acc.plus(val || 0) : acc, new Decimal(0));
    const remaining = totalWithDiscount.minus(sumManual);

    if (autoIndexes.length > 0) {
        const base = remaining.div(autoIndexes.length).toDecimalPlaces(2, Decimal.ROUND_FLOOR);
        const totalBase = base.mul(autoIndexes.length);
        const remainder = remaining.minus(totalBase);

        autoIndexes.forEach(i => values[i] = base.toFixed(2));
        values[autoIndexes[autoIndexes.length - 1]] = new Decimal(values[autoIndexes[autoIndexes.length - 1]]).plus(remainder).toFixed(2);
    }

    return values;
};

const handlePreventNegativeInput = (event) => {
    if (event.key === '-' || event.key === 'e' || event.key === 'E') {
        event.preventDefault();
    }
};

const handlePreventNegativeAndDecimalsInput = (event) => {
    if (['-', 'e', 'E', '.', ','].includes(event.key)) {
        event.preventDefault();
    }
};

const validateNonNegativeAndDecimals = (val, { integersOnly = false } = {}) => {
    if (val === '') return '';
    const num = Number(val.toString().replace(',', '.'));

    if (isNaN(num) || num < 0) return null;
    if (integersOnly && !Number.isInteger(num)) return null;

    return val;
};

const InvoicePaymentConditions = ({ data, setData, onValidityChange, partsValue, worksValue, onDiscountChange, onInstallmentsQtyChange,
    onInstallmentsAttributesChange, onPerformedDateChange, clientHasRequiredData, onAutomatedChange }) => {
    const [discountFixedError, setDiscountFixedError] = useState(false);
    const [discountPercentError, setDiscountPercentError] = useState(false);
    const [interestError, setInterestError] = useState(false);
    const [fineError, setFineError] = useState(false);
    const [installmentsError, setInstallmentsError] = useState([]);

    const isAutomatedPayment = PaymentsTypeOptions.find(opt => opt.value === data.paymentType)?.group === 'automated';
    const hasInstallmentsError = Array.isArray(installmentsError) && installmentsError.some(err => err);
    const prevDiscountRef = useRef(data.discountValue);

    useEffect(() => {
        const automated = PaymentsTypeOptions.find(opt => opt.value === data.paymentType)?.group === 'automated';
        onAutomatedChange?.(automated);
    }, [data.paymentType]);

    useEffect(() => {
        if (data.installments === 'À vista') {
            if (data.valuePayment && data.dueDatePayment) {
                onInstallmentsAttributesChange?.([
                    { value: data.valuePayment, due_date: data.dueDatePayment.format("YYYY-MM-DD") }
                ]);
            }
        } else if (data.values?.length && data.dueDates?.length) {
            const installmentsArr = data.values.map((v, i) => {
                if (!v || !data.dueDates[i]) return null;
                return { value: v, due_date: data.dueDates[i].format("YYYY-MM-DD") };
            }).filter(Boolean);
            onInstallmentsAttributesChange?.(installmentsArr);
        }
    }, [data.valuePayment, data.dueDatePayment, data.values, data.dueDates]);

    useEffect(() => {
        const errorsValidations = fineError || interestError || discountFixedError || discountPercentError;
        const isValid = data.installments === 'À vista'
            ? !!data.paymentType && !!data.valuePayment && !!data.dueDatePayment && !errorsValidations
            : !!data.paymentType && Array.isArray(data.values) && data.values.every(v => v !== '') &&
            Array.isArray(data.dueDates) && data.dueDates.every(d => d !== null) && !errorsValidations && !hasInstallmentsError;

        onValidityChange?.(isValid);
    }, [data, fineError, interestError, discountFixedError, discountPercentError, installmentsError, onValidityChange,]);

    useEffect(() => {
        const { total, discountAmount, totalWithDiscount } = calculateDiscount(partsValue, worksValue, data.discount, data.discountValue);
        onDiscountChange?.(discountAmount.toFixed(2));

        if (data.installments === 'À vista') {
            setData(prev => ({
                ...prev,
                valuePayment: totalWithDiscount.toFixed(2),
            }));
            return;
        }

        const prevDiscount = prevDiscountRef.current;
        const currentDiscount = data.discountValue;

        if (prevDiscount !== currentDiscount) {
            prevDiscountRef.current = currentDiscount;

            if (Array.isArray(data.values) && data.values.length) {
                const qty = data.values.length;
                const totalDecimal = new Decimal(totalWithDiscount);

                const base = totalDecimal.div(qty).toDecimalPlaces(2, Decimal.ROUND_FLOOR);
                const remainder = totalDecimal.minus(base.mul(qty));

                const newValues = Array(qty).fill(base.toFixed(2));
                newValues[qty - 1] = new Decimal(newValues[qty - 1]).plus(remainder).toFixed(2);

                setData(prev => ({
                    ...prev,
                    values: newValues,
                    manualValues: Array(qty).fill(false),
                }));
            }
        }
    }, [partsValue, worksValue, data.discount, data.discountValue, data.installments]);

    const handleChangeInstallments = (event) => {
        const selected = event.target.value;
        const qty = selected === 'À vista' ? 1 : parseInt(selected, 10);
        onInstallmentsQtyChange?.(qty);

        const { totalWithDiscount } = calculateDiscount(partsValue, worksValue, data.discount, data.discountValue);
        const values = Array(qty).fill('0.00');
        const manual = Array(qty).fill(false);
        const recalculated = recalcInstallments(values, manual, totalWithDiscount);

        setData(prev => ({
            ...prev,
            installments: selected,
            manualValues: manual,
            values: recalculated,
            dueDates: Array(qty).fill(null),
            valuePayment: selected === 'À vista' ? recalculated[0] : '',
            dueDatePayment: null,
        }));

        setInstallmentsError(Array(qty).fill(false));
    };

    const handleChangeValue = (index, newValue) => {
        const manualCopy = [...(data.manualValues || [])];
        const updatedValues = [...data.values];
        const updatedErrors = [...installmentsError];
        const { totalWithDiscount } = calculateDiscount(partsValue, worksValue, data.discount, data.discountValue);

        const valueDecimal = new Decimal(newValue || 0);
        const totalDecimal = new Decimal(totalWithDiscount);

        if (valueDecimal.gt(totalDecimal)) {
            updatedErrors[index] = true;
            setInstallmentsError(updatedErrors);


            setData(prev => ({
                ...prev,
                values: updatedValues.map((v, i) => (i === index ? newValue : v)),
            }));
            return;
        } else {
            updatedErrors[index] = false;
        }

        manualCopy[index] = true;
        updatedValues[index] = newValue;

        const hasAnyError = updatedErrors.some(err => err);
        const recalculated = hasAnyError
            ? updatedValues
            : recalcInstallments(updatedValues, manualCopy, totalWithDiscount);

        setInstallmentsError(updatedErrors);
        setData(prev => ({
            ...prev,
            values: recalculated,
            manualValues: manualCopy,
        }));
    };

    useEffect(() => {
        if (isAutomatedPayment) {
            const { totalWithDiscount } = calculateDiscount(partsValue, worksValue, data.discount, data.discountValue);

            setData(prev => ({
                ...prev,
                installments: 'À vista',
                values: [totalWithDiscount.toFixed(2)],
                manualValues: [false],
                dueDates: [prev.dueDatePayment || null],
                valuePayment: totalWithDiscount.toFixed(2),
            }));

            onInstallmentsQtyChange?.(1);
            onInstallmentsAttributesChange?.([{
                value: totalWithDiscount.toFixed(2),
                due_date: data.dueDatePayment ? data.dueDatePayment.format('YYYY-MM-DD') : null
            }]);
        }
    }, [isAutomatedPayment]);

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                {isAutomatedPayment && !clientHasRequiredData && (
                    <DataRequiredMessageError clientId={data?.client?.id} />
                )}
                <Box>
                    <TooltipCompetenceDate />
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                        <DatePicker
                            value={data.competenceDate || null}
                            onChange={(newValue) => {
                                setData((prevState) => ({
                                    ...prevState,
                                    competenceDate: newValue
                                }));
                                onPerformedDateChange?.(newValue ? newValue.format('YYYY-MM-DD') : null);
                            }}
                            sx={{ maxWidth: '16.5rem' }}
                        />
                    </LocalizationProvider>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <FormLabel sx={{ color: 'text.primary' }}><b>Forma de pagamento</b> <FormLabel sx={{ color: 'error.main' }}><b>*</b></FormLabel></FormLabel>
                        {isAutomatedPayment && (
                            <Chip color="automatic" label="Automático" />
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: '1.5rem', width: '100%' }}>
                        <FormControl sx={{ width: '16.5rem' }}>
                            <Select
                                value={data.paymentType || ''}
                                onChange={(e) =>
                                    setData(prev => ({ ...prev, paymentType: e.target.value }))
                                }
                                displayEmpty
                                renderValue={(selected) => {
                                    if (!selected) return <Typography sx={{ color: 'text.disabled' }}>Selecionar</Typography>;
                                    const found = PaymentsTypeOptions.find(opt => opt.value === selected);
                                    return found ? found.label : selected;
                                }}
                            >
                                <Tooltip title={
                                    <>
                                        O sistema vai executar automaticamente o fluxo de envio e
                                        atualização da fatura de acordo com a opção selecionada
                                    </>
                                }
                                    placement='right' arrow>
                                    <ListSubheader sx={{ fontWeight: 'bold', color: 'success.main', bgcolor: 'rgba(0, 128, 0, 0.05)' }}>
                                        Automático (Produtivo)
                                    </ListSubheader>
                                </Tooltip>
                                {groupedOptions.automated.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                                <Tooltip title={
                                    <>
                                        Você precisa realizar o envio e atualização da fatura de acordo
                                        com a opção selecionada
                                    </>
                                }
                                    placement='right' arrow>
                                    <ListSubheader sx={{ fontWeight: 'bold', color: 'text.primary', bgcolor: 'action.hover' }}>
                                        Manual (Outros bancos)
                                    </ListSubheader>
                                </Tooltip>
                                {groupedOptions.manual.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: '5rem', maxWidth: '9rem' }}>
                            <Tooltip
                                title={installmentsTooltipMap[data.paymentType] || ""}
                                placement="right"
                                arrow
                                disableHoverListener={!installmentsTooltipMap[data.paymentType]}
                            >
                                <span>
                                    <Select
                                        value={isAutomatedPayment ? 'À vista' : (data.installments || 'À vista')}
                                        onChange={handleChangeInstallments}
                                        disabled={isAutomatedPayment}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 200,
                                                },
                                            },
                                        }}
                                    >
                                        {PaymentInstallments.map((option, index) => (
                                            <MenuItem key={index} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </span>
                            </Tooltip>
                        </FormControl>
                    </Box>
                </Box>
                {data.installments === 'À vista' ? (
                    <Box sx={{ display: 'flex', gap: '1.5rem' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FormLabel sx={{ mb: 1.3, color: 'text.primary' }}><b>Valor</b> <FormLabel sx={{ color: 'error.main' }}><b>*</b></FormLabel></FormLabel>
                            <Tooltip
                                title={"O valor não pode ser alterado para garantir o pagamento integral da fatura"}
                                placement='top' arrow
                                slotProps={{
                                    tooltip: {
                                        sx: {
                                            textAlign: 'center',
                                            fontSize: '0.8rem',
                                        },
                                    },
                                }}
                            >
                                <CurrencyTextField
                                    currencySymbol=""
                                    variant="outlined"
                                    outputFormat="number"
                                    decimalCharacter=","
                                    digitGroupSeparator="."
                                    disabled
                                    sx={{ maxWidth: '11rem' }}
                                    inputProps={{
                                        style: { textAlign: 'left' }
                                    }}
                                    value={data.valuePayment || ''}
                                    onChange={(e) => {
                                        const raw = e.target.value
                                        setData((prev) => ({ ...prev, valuePayment: raw }));
                                        onInstallmentsAttributesChange?.([
                                            { value: raw, due_date: data.dueDatePayment ? data.dueDatePayment.format('YYYY-MM-DD') : null }
                                        ]);
                                    }}
                                />
                            </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FormLabel sx={{ mb: 1.3, color: 'text.primary' }}><b>Vencimento</b> <FormLabel sx={{ color: 'error.main' }}><b>*</b></FormLabel></FormLabel>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                <DatePicker
                                    sx={{ maxWidth: '16.5rem' }}
                                    value={data.dueDatePayment || null}
                                    onChange={(newValue) => {
                                        setData((prev) => ({ ...prev, dueDatePayment: newValue }));
                                        onInstallmentsAttributesChange?.([
                                            { value: data.valuePayment, due_date: newValue ? newValue.format('YYYY-MM-DD') : null }
                                        ]);
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {data.values.map((value, index) => (
                            <Box>
                                <Box key={index} sx={{ display: 'flex', gap: '1.5rem' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        {index === 0 && <FormLabel sx={{ mb: 1.3, color: 'text.primary' }}><b>Valor</b> <FormLabel sx={{ color: 'error.main' }}><b>*</b></FormLabel></FormLabel>}
                                        <Tooltip
                                            title={index === data.values.length - 1 ? "O valor não pode ser alterado para garantir o pagamento integral da fatura" : ""}
                                            placement='top' arrow
                                            slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        textAlign: 'center',
                                                        fontSize: '0.8rem',
                                                    },
                                                },
                                            }}
                                        >
                                            <CurrencyTextField
                                                currencySymbol=""
                                                variant="outlined"
                                                outputFormat="number"
                                                decimalCharacter=","
                                                digitGroupSeparator="."
                                                label={`Parcela ${index + 1}`}
                                                sx={{ maxWidth: "11rem" }}
                                                placeholder="0,00"
                                                disabled={index === data.values.length - 1 || (installmentsError.some(Boolean) && !installmentsError[index])}
                                                value={value}
                                                error={installmentsError[index]}
                                                onChange={(e, val) => handleChangeValue(index, val)}
                                            />
                                        </Tooltip>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        {index === 0 && <FormLabel sx={{ mb: 1.3, color: 'text.primary' }}><b>Vencimento</b> <FormLabel sx={{ color: 'error.main' }}><b>*</b></FormLabel></FormLabel>}
                                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                            <DatePicker
                                                sx={{ maxWidth: '16.5rem' }}
                                                label={`Vencimento ${index + 1}`}
                                                value={data.dueDates[index] || null}
                                                onChange={(newValue) => {
                                                    setData((prevState) => {
                                                        const qty = prevState.values?.length || 1;
                                                        const updatedDates = [...(prevState.dueDates || [])];

                                                        while (updatedDates.length < qty) {
                                                            updatedDates.push(null);
                                                        }

                                                        updatedDates[index] = newValue;

                                                        if (updatedDates[0]) {
                                                            for (let i = 1; i < qty; i++) {
                                                                if (!updatedDates[i]) {
                                                                    updatedDates[i] = dayjs(updatedDates[0]).add(i, 'month');
                                                                }
                                                            }
                                                        }

                                                        const formattedDates = updatedDates.map(d => d ? d.format('YYYY-MM-DD') : null);

                                                        const attributes = prevState.values.map((val, i) => ({
                                                            value: val,
                                                            due_date: formattedDates[i]
                                                        }));

                                                        onInstallmentsAttributesChange?.(attributes);

                                                        return {
                                                            ...prevState,
                                                            dueDates: updatedDates,
                                                        };
                                                    });
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Box>
                                </Box>
                                {installmentsError[index] && (
                                    <FormHelperText variant="helperError" sx={{ px: '0.875rem', minHeight: '1.25rem', }}>
                                        Deve ser menor ou igual ao valor total.
                                    </FormHelperText>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <FormLabel sx={{ color: 'text.primary' }}><b>Desconto</b></FormLabel>
                        <Tooltip
                            title={hasInstallmentsError ? "Não é possível aplicar desconto enquanto houver erro em alguma parcela." : ""}
                            placement='right'
                            arrow
                        >
                            <FormControl sx={{ maxWidth: '11rem', width: '11rem' }}>
                                <Select
                                    value={data.discount || ''}
                                    onChange={(e) => {
                                        const newType = e.target.value;
                                        setData(prevState => ({
                                            ...prevState,
                                            discount: newType,
                                            discountValue: '',
                                            discountValueInput: '',
                                            discountPercentInput: '',
                                        }));
                                        setDiscountFixedError(false);
                                        setDiscountPercentError(false);
                                    }}
                                    displayEmpty
                                    disabled={hasInstallmentsError}
                                    renderValue={(selected) => {
                                        if (!selected) {
                                            return <Typography sx={{ color: 'text.disabled' }}>Selecionar</Typography>;
                                        }
                                        const found = DiscountOptions.find(opt => opt.value === selected);
                                        return found ? found.label : selected;
                                    }}
                                >
                                    {DiscountOptions.map((option, index) => (
                                        <MenuItem key={index} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <FormLabel sx={{ color: 'text.primary', visibility: 'hidden' }}>Desconto</FormLabel>
                        {data.discount && (
                            data.discount === "fixed" ? (
                                <Box sx={{ display: 'inline-flex', flexDirection: 'column', }}>
                                    <CurrencyTextField
                                        currencySymbol=""
                                        variant="outlined"
                                        outputFormat="number"
                                        decimalCharacter=","
                                        digitGroupSeparator="."
                                        placeholder="0,00"
                                        sx={{ maxWidth: '13.75rem' }}
                                        value={data.discountValueInput ?? data.discountValue ?? ''}
                                        error={discountFixedError}
                                        disabled={hasInstallmentsError}
                                        onChange={(event, value) => {
                                            const discountValue = Number(value || 0);

                                            const totalInvoiceValue = new Decimal(partsValue).plus(worksValue).toNumber();

                                            if (discountValue >= totalInvoiceValue) {
                                                setDiscountFixedError(true);
                                                setData(prev => ({
                                                    ...prev,
                                                    discountValueInput: value,
                                                    discountValue: '',
                                                }));
                                                return;
                                            } else {
                                                setDiscountFixedError(false);
                                            }

                                            setData(prevState => ({
                                                ...prevState,
                                                discountValueInput: value,
                                                discountValue: value,
                                            }));
                                        }}
                                    />
                                    {discountFixedError && (
                                        <FormHelperText variant="helperError" sx={{ px: '0.875rem' }}>
                                            Deve ser menor que o valor cobrado.
                                        </FormHelperText>
                                    )}
                                </Box>
                            ) : (
                                <Box>
                                    <TextField
                                        type="number"
                                        placeholder="0,00"
                                        sx={{ maxWidth: '13.75rem' }}
                                        value={data.discountPercentInput ?? data.discountValue ?? ''}
                                        onKeyDown={handlePreventNegativeInput}
                                        error={discountPercentError}
                                        disabled={hasInstallmentsError}
                                        onChange={(e) => {
                                            const rawValue = e.target.value;
                                            const num = Number(rawValue.replace(',', '.'));

                                            if (!isNaN(num) && num >= 0) {
                                                if (num >= 100) {
                                                    setDiscountPercentError(true);
                                                    setData(prev => ({
                                                        ...prev,
                                                        discountPercentInput: rawValue,
                                                        discountValue: '',
                                                    }));
                                                } else {
                                                    setDiscountPercentError(false);
                                                    setData(prev => ({
                                                        ...prev,
                                                        discountPercentInput: rawValue,
                                                        discountValue: num,
                                                    }));
                                                }
                                            }
                                        }}
                                    />
                                    {discountPercentError && (
                                        <FormHelperText variant="helperError" sx={{ px: '0.875rem' }}>
                                            Deve ser menor que 100%.
                                        </FormHelperText>
                                    )}
                                </Box>
                            ))}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: '1.5rem' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <TooltipInterest />
                        <TextField
                            type="number"
                            variant="outlined"
                            placeholder="0,00"
                            sx={{ maxWidth: '11rem' }}
                            value={data.interest || ''}
                            onKeyDown={handlePreventNegativeAndDecimalsInput}
                            error={interestError}
                            onChange={(e) => {
                                const validated = validateNonNegativeAndDecimals(e.target.value, { integersOnly: true });
                                if (validated !== null) {
                                    setInterestError(validated >= 100);
                                    setData(prev => ({ ...prev, interest: validated }));
                                }
                            }}
                        />
                        {interestError && (
                            <FormHelperText variant="helperError" sx={{ px: '0.875rem' }}>
                                Deve ser menor que 100%.
                            </FormHelperText>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <TooltipFine />
                        <CurrencyTextField
                            currencySymbol=""
                            variant="outlined"
                            outputFormat="number"
                            decimalCharacter=","
                            digitGroupSeparator="."
                            placeholder="0,00"
                            sx={{ maxWidth: '11rem' }}
                            value={data.fine || ''}
                            error={fineError}
                            onChange={(event, value) => {
                                const fineValue = Number(value || 0);

                                const totalInvoiceValue = new Decimal(partsValue).plus(worksValue).toNumber();

                                if (fineValue >= totalInvoiceValue) {
                                    setFineError(true);
                                    return;
                                } else {
                                    setFineError(false);
                                }

                                setData((prevState) => ({
                                    ...prevState,
                                    fine: value,
                                }));
                            }}
                        />
                        {fineError && (
                            <FormHelperText variant="helperError" sx={{ px: '0.875rem' }}>
                                Deve ser menor que o valor cobrado.
                            </FormHelperText>
                        )}
                    </Box>
                </Box>
                <Box>
                    <Typography variant='caption' sx={{ color: 'error.main' }}><b>*</b> Indica um campo obrigatório </Typography>
                </Box>
            </Box >
        </>
    )
}

export default InvoicePaymentConditions; 